import { useEffect, useState } from 'react';

import { useModalContext } from '../../../../context/Modal';
import { useNewStrategyContext } from '../../NewStrategyContext';

import { ColorButton } from '../../../UI';

import { useSort } from '../../../../hooks/useSort';
import { useTutorialOverlay } from '../../../../hooks/useTutorialOverlay';

import { formatDate } from '../../../../helpers/functions';

import { COLUMNS_SUMMARY } from '../../../../constants/lists';

import { t } from 'i18next';
import { useUserStrategies } from '../../../../context/UserStrategies';
import { TTickerCurrency } from '../../../../types/model';
import { UpgradeSubscription } from '../../../UpgradeSubscription/UpgradeSubscription';
import { useAuth } from '../../../../context/Auth';
import { useLocation } from 'react-router-dom';
import { logger } from '../../../../helpers/logger';
import {
  AssetOptionEnum,
  ConnectionTypeEnum,
} from '../../../../constants/enums';
import { UpsertAsset } from '../../../Asset/UpsertAsset';
import { Portfolio } from '../../../Widget/Portfolio/Portfolio';

const tooltipsArray = [
  <span>
    Start creating your portfolio by adding your first strategyAsset to your
    strategy
  </span>,
];

export const Summary = () => {
  const { state } = useLocation();

  const { user } = useAuth();
  const { skipStrategy } = useNewStrategyContext();
  const { dispatch: dispatchModal, closeModal } = useModalContext();
  const [_isLoading, setIsLoading] = useState(false);

  const {
    isTooltipIconVisible,
    newStrategy,
    selectedConnectionType,
    dispatch,
  } = useNewStrategyContext();

  const { draftStrategy, draftCloneStrategy, strategies } = useUserStrategies();

  const { assets } = newStrategy;

  useEffect(() => {
    dispatch({ isDisabledNext: assets.length === 0 });
    return () => dispatch({ isDisabledNext: false });
  }, [assets.length]);

  const {
    TutorialOverlay,
    tooltips: [],
  } = useTutorialOverlay({
    isTooltipIconVisible,
    isTutorialShown:
      strategies.length === 0 && !draftStrategy && !draftCloneStrategy,
    tooltipsArray,
    tooltipClassname: 'ml-1',
    onCloseTutorial: () => dispatch({ isTooltipIconVisible: false }),
  });

  const { type, getSortValue } = useSort([...COLUMNS_SUMMARY]);

  const currency =
    newStrategy.reportingCurrency.code!.toLocaleLowerCase() as TTickerCurrency;

  // const holdings = assets.reduce(
  //   (total, { asset: { history }, entryPrice, unit }) =>
  //     total + unit * (history[0]?.[currency] ?? entryPrice),
  //   0
  // );

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const openAddAssetModal = (
    walletAccount?: string,
    walletConnection?: ConnectionTypeEnum.WALLET
  ) => {
    user!.membership.membershipPlan.features.strategyAssets === -1 ||
    user!.membership.membershipPlan.features.strategyAssets > assets.length
      ? dispatchModal({
          className: 'justify-between',
          element: (
            <UpsertAsset
              strategyId={newStrategy.strategyId}
              selectedConnectionType={
                walletConnection ?? selectedConnectionType
              }
              walletAccount={walletAccount}
              onCloseClick={closeModal}
              skipStrategy={skipStrategy}
              action={'CREATE'}
            />
          ),
          title: t('strategies.newStrategy.addAsset'),
          z: 70,
        })
      : setIsUpgradeModalOpen(true);
  };

  useEffect(() => {
    if (state?.account && newStrategy.strategyId) {
      logger.log('NewStrategy:Summary', { state });
      openAddAssetModal(state.account, ConnectionTypeEnum.WALLET);

      window.history.replaceState({}, document.title);
    } else if (newStrategy.assets.length === 0) {
      openAddAssetModal();
    }
  }, []);

  if (type === 'type of asset') {
    assets.sort((a, b) => {
      let [arg1, arg2] = [
        a.strategyAssetCategory.title,
        b.strategyAssetCategory.title,
      ];

      arg1 === arg2 && ([arg1, arg2] = [a.name, b.name]);

      return getSortValue(arg1, arg2);
    });
  } else if (type === 'asset name') {
    assets.sort((a, b) => getSortValue(a.name, b.name));
  } else if (type === 'quantity') {
    assets.sort((a, b) => getSortValue(a.unit, b.unit));
  } else if (type === 'entry price') {
    assets.sort((a, b) => getSortValue(a.entryPrice, b.entryPrice));
  } else if (type === 'invested') {
    assets.sort((a, b) =>
      getSortValue(a.unit * a.entryPrice, b.unit * b.entryPrice)
    );
  } else if (type === 'bank/wallet') {
    assets.sort((a, b) => {
      const valueA = a.bank || a.wallet || '';
      const valueB = b.bank || b.wallet || '';

      return getSortValue(valueA, valueB);
    });
  }

  return (
    <div
      className={`
        flex
        flex-col
        px-10
        pt-5
        w-full
        h-full
        text-black
      `}
    >
      <TutorialOverlay />
      <UpgradeSubscription
        onCloseClick={() => setIsUpgradeModalOpen(false)}
        onUpgradeClick={() => skipStrategy?.()}
        isOpen={isUpgradeModalOpen}
        feature="ASSETS"
        viewMode="modal"
      />
      {/* strategy info */}
      <div
        className={`
          flex
          items-center
          border
          border-grey-30
          h-20
          rounded-xl
          shadow-md
          mt-5
          px-5
          dark:bg-grey-90
          dark:border-grey-80
          dark:text-white
        `}
      >
        {/* COLOR & NAME */}
        <div className="flex items-center h-20">
          <ColorButton
            radius={4}
            isReadOnly={true}
            color={newStrategy.strategyColor!}
          />
          <div className="pl-3 text-lg font-medium">
            {newStrategy.strategyName}
          </div>
        </div>

        {/* TAGS */}
        <div className="hidden lg:flex items-center mr-auto">
          {newStrategy?.tagList?.length &&
            newStrategy.tagList.map((tag, i) => (
              <div
                key={tag + '_' + i}
                className="uppercase rounded-xl text-sm font-semibold bg-grey-5 px-2 py-2 ml-5 truncate dark:bg-grey-80"
              >
                {tag}
              </div>
            ))}
        </div>

        {/* TIMEFRAME */}
        <div className="md:flex hidden items-center justify-end mx-5">
          <span className="text-grey-50 font-light mr-2">
            {t('strategies.newStrategy.summary.timeframe')}:
          </span>
          {`${formatDate(newStrategy.strategyTimeframe[0])} - ${formatDate(
            newStrategy.strategyTimeframe[1]
          )}`}
        </div>

        {/* CURRENCY */}
        <div className="md:flex mx-5 hidden ">
          <span className="text-grey-50 font-light mr-2">
            {t('strategies.newStrategy.summary.reportingCurrency')}:
          </span>
          <span className={'uppercase'}>{currency}</span>
        </div>
      </div>

      {/*<div className="flex gap-3">*/}
      {/*  /!* HOLDINGS *!/*/}
      {/*  <div className="flex flex-col pl-5 py-2 bg-grey-5 dark:bg-grey-80 my-5 w-56 rounded-2xl ">*/}
      {/*    <div className="text-grey-70 dark:text-grey-30 capitalize-first">*/}
      {/*      {t('strategies.newStrategy.summary.totalHoldings')}*/}
      {/*    </div>*/}
      {/*    <CustomNumber*/}
      {/*      allowNegative={false}*/}
      {/*      className="font-semibold text-lg dark:text-white"*/}
      {/*      displayType="text"*/}
      {/*      suffix={getSymbol(newStrategy.reportingCurrency.code)}*/}
      {/*      value={holdings}*/}
      {/*    />*/}
      {/*  </div>*/}
      {/*  <div className="flex flex-col pl-5 py-2 bg-grey-5 dark:bg-grey-80 my-5 w-56 rounded-2xl ">*/}
      {/*    <div className="text-grey-70 dark:text-grey-30 capitalize-first">*/}
      {/*      {t('common.asset.1')}*/}
      {/*    </div>*/}
      {/*    <div className="font-semibold text-lg dark:text-white">*/}
      {/*      {newStrategy.assets.length}*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</div>*/}

      <div className={'h-full-40 mt-8'}>
        <div
          className={`
        relative
        flex
        flex-col
        h-full
        dark:bg-grey-90
        dark:text-white
        bg-white
        border
        border-grey-30
        dark:border-grey-70
        rounded-xl
        shadow-md
        `}
        >
          <div
            className="
          flex
          flex-shrink-0
          items-center
          justify-between
          w-full
          border-b
          border-grey-10
          p-3.5
          dark:bg-grey-90
          dark:border-grey-70
          rounded-t-xl
        "
          >
            <span className="text-sm font-semibold uppercase ml-1 mr-2">
              {'Strategy Assets'}
            </span>
          </div>

          <div className={'flex flex-1 w-full h-full overflow-auto'}>
            <div className={'w-full h-full flex flex-col'}>
              <Portfolio
                strategyId={newStrategy.strategyId}
                loading={setIsLoading}
                width={100}
                height={2}
                standAlone={true}
                useCheckboxes={false}
                columns={[
                  'ASSET',
                  'QUANTITY',
                  'ENTRY PRICE',
                  'INVESTED',
                  'INITIAL_ALLOCATION',
                ]}
                menuOptions={[
                  AssetOptionEnum.EDIT_ASSET,
                  AssetOptionEnum.DUPLICATE,
                  AssetOptionEnum.DELETE,
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
